// ═══════════════════════════════════════════════════════════════════════════
//  Sheet — one dialog primitive, two presentations.
//  Mobile blueprint Phase 4 (God 2026-09-04).
//
//  On a phone a centred modal is the wrong shape: it lands under the thumb's
//  reach, its close button sits at the far top corner, and anything taller than
//  the viewport becomes a scroll trap inside a scrolling page. A bottom sheet
//  starts where the thumb already is and grows upward.
//
//  On desktop the same component is the centred modal the console already uses,
//  so a caller writes ONE dialog and both surfaces are correct — the same rule
//  DataTable follows.
//
//  Details that are load-bearing, not polish:
//   * Body scroll is locked while open, or iOS scrolls the page BEHIND the
//     sheet and the reader loses their place in the list they came from.
//   * The panel is a scroll container with its own overscroll-behavior:contain,
//     so flicking past its end does not start dragging the page.
//   * Bottom padding carries env(safe-area-inset-bottom): without it the last
//     row and the action bar sit under the iPhone home indicator.
//   * Escape closes, and so does the backdrop — but a pointerdown that STARTED
//     inside the panel and ended on the backdrop does not, or a fast drag on a
//     slider throws the sheet away.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useCallback, useEffect, useRef } from 'react';
import { useViewport } from '../../hooks/useBreakpoint';

export interface SheetProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  /** Pinned to the bottom, above the safe-area inset — confirm/cancel live here. */
  actions?: React.ReactNode;
  children: React.ReactNode;
  /** Desktop max width. Phones always use the full width. */
  width?: number;
  /** Phone height as a share of the viewport. */
  maxHeightVh?: number;
}

export const Sheet: React.FC<SheetProps> = ({
  open, onClose, title, actions, children, width = 560, maxHeightVh = 88,
}) => {
  const { isPhone } = useViewport();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const downInsideRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  // A drag that began inside the panel must not close the sheet when the
  // pointer happens to come up over the backdrop.
  const onBackdropDown = useCallback((e: React.PointerEvent) => {
    downInsideRef.current = !!panelRef.current?.contains(e.target as Node);
  }, []);
  const onBackdropUp = useCallback(() => {
    if (!downInsideRef.current) onClose();
    downInsideRef.current = false;
  }, [onClose]);

  if (!open) return null;

  return (
    <div
      onPointerDown={onBackdropDown}
      onPointerUp={onBackdropUp}
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        display: 'flex',
        alignItems: isPhone ? 'flex-end' : 'center',
        justifyContent: 'center',
        background: 'rgba(4,7,14,0.62)',
        WebkitBackdropFilter: 'blur(6px)', backdropFilter: 'blur(6px)',
        animation: 'jcSheetFade 160ms ease both',
      }}>
      <style>{`
        @keyframes jcSheetFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes jcSheetUp   { from { transform: translateY(14px); opacity: .6 }
                                 to   { transform: none; opacity: 1 } }
      `}</style>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        // MARK FIRST, THEN STOP (God 2026-09-22: "har bots may problem hay,
        // function botam may"). The backdrop's guard sets downInsideRef from
        // ITS OWN pointerdown via contains() -- but this stopPropagation meant
        // that pointerdown never reached the backdrop, so the flag stayed
        // false. pointerUP was never stopped, so it DID reach the backdrop,
        // read a false flag and called onClose(). Every click inside the sheet
        // closed it: the button's own handler ran and the modal vanished in the
        // same gesture, on every bot and in all three modals that ride Sheet.
        // Setting the flag here keeps the drag-out protection the guard was
        // written for AND keeps the panel from closing itself.
        onPointerDown={e => { downInsideRef.current = true; e.stopPropagation(); }}
        className="jc-glass"
        style={{
          width: isPhone ? '100%' : `min(${width}px, calc(100vw - 32px))`,
          maxHeight: isPhone ? `${maxHeightVh}dvh` : 'min(86dvh, 900px)',
          display: 'flex', flexDirection: 'column',
          borderRadius: isPhone ? 'var(--jc-r-lg) var(--jc-r-lg) 0 0' : 'var(--jc-r-lg)',
          borderBottom: isPhone ? 'none' : undefined,
          boxShadow: 'var(--jc-shadow-3)',
          animation: 'jcSheetUp 200ms cubic-bezier(.2,.8,.2,1) both',
          overflow: 'hidden',
        }}>

        {/* Grab handle — phones only. It is the affordance that says "this came
            from the bottom and goes back there". */}
        {isPhone && (
          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
            <div style={{ width: 38, height: 4, borderRadius: 99,
              background: 'var(--jc-hairline-hi)' }} />
          </div>
        )}

        {title != null && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 'var(--jc-s-2)',
            padding: isPhone ? '10px 16px 12px' : '14px 18px',
            borderBottom: '1px solid var(--jc-hairline)',
          }}>
            <div style={{ flex: 1, fontSize: 12.5, fontWeight: 800, letterSpacing: .6,
              textTransform: 'uppercase', color: 'var(--jc-ink)' }}>
              {title}
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="jc-press"
              style={{
                width: 30, height: 30, borderRadius: 'var(--jc-r-sm)', cursor: 'pointer',
                border: '1px solid var(--jc-hairline)', background: 'var(--jc-surface)',
                color: 'var(--jc-text-dim)', fontSize: 15, lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>×</button>
          </div>
        )}

        <div style={{
          flex: 1, minHeight: 0, overflowY: 'auto',
          overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch',
          padding: isPhone ? '12px 14px' : '16px 18px',
        }}>
          {children}
        </div>

        {actions != null && (
          <div style={{
            borderTop: '1px solid var(--jc-hairline)',
            background: 'var(--jc-raised)',
            padding: isPhone
              ? '10px 14px calc(10px + env(safe-area-inset-bottom))'
              : '12px 18px',
            display: 'flex', gap: 'var(--jc-s-2)', justifyContent: 'flex-end',
          }}>
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default Sheet;
